import ApiService from "@/service/api/ApiService";
import { catchError, map } from "rxjs/operators";
import { Observable, of } from "rxjs";
import LectureInfoModel from "@/service/model/lecture/LectureInfoModel";
import InquiredLectureListModel from "@/service/model/lecture/InquiredLectureListModel";
import InquiredLectureDataModel from "@/service/model/lecture/InquiredLectureDataModel";
import LectureProgressRateByUserDataModel from "@/service/model/lecture/LectureProgressRateByUserDataModel";

class LectureService extends ApiService {
  /**
   * 사용자의 Lecture 리스트 조회
   * @param userId
   */
  public retrieveLectureIntoByUserId(
    userId: number
  ): Observable<LectureInfo[]> {
    return this.get<LectureInfo[]>("/lecture/list/user", { userId }, true).pipe(
      map((lectureInfoList: LectureInfo[]) =>
        lectureInfoList.map(lectureInfo => LectureInfoModel.create(lectureInfo))
      ),
      catchError(() => of([]))
    );
  }

  /**
   * 기업 Lecture 리스트 조회
   * @param organizationId
   * @param filter
   * @param keyword
   * @param sortBy
   * @param desc
   */
  public retrieveLectureIntoByOrganizationId(
    organizationId: number,
    filter?: string,
    keyword?: string,
    sortBy?: string,
    desc?: boolean
  ): Observable<LectureInfo[]> {
    return this.get<LectureInfo[]>(
      "/lecture/list/organization",
      { organizationId, filter, keyword, sortBy, desc },
      true
    ).pipe(
      map((lectureInfoList: LectureInfo[]) =>
        lectureInfoList.map(lectureInfo => LectureInfoModel.create(lectureInfo))
      ),
      catchError(() => of([]))
    );
  }

  /**
   * 전체 Lecture 목록 조회
   * @param page
   * @param itemSize
   * @param lectureSearchType
   * @param keyword
   */
  public retrieveLectureList(
    page: number,
    itemSize: number,
    lectureSearchType?: string,
    keyword?: string
  ): Observable<InquiredLectureListModel> {
    return this.get<PageData<InquiredLectureData>>(
      "/lecture/list",
      {
        page,
        itemSize,
        lectureSearchType,
        keyword
      },
      true
    ).pipe(
      map((course: PageData<InquiredLectureData>) =>
        InquiredLectureListModel.create(course)
      ),
      catchError(() => of(new InquiredLectureListModel()))
    );
  }

  /**
   * 특정 Lecture 상세 조회
   * @param id
   */
  public retrieveLectureDetail(id: number): Observable<InquiredLectureData> {
    return this.get<InquiredLectureData>(
      "/lecture/detail",
      {
        id
      },
      true
    ).pipe(
      map((lecture: InquiredLectureData) =>
        InquiredLectureDataModel.create(lecture)
      ),
      catchError(() => of(new InquiredLectureDataModel()))
    );
  }

  /**
   * Lecture 생성
   * @param lectureData
   */
  public createLecture(lectureData: LectureDataForCreate): Observable<boolean> {
    return this.post<boolean>("/lecture", lectureData, true).pipe(
      map((isSuccess: boolean) => isSuccess),
      catchError(() => of(false))
    );
  }

  /**
   *  Lecture 수정
   * @param lectureDataForUpdate
   */
  public updateLecture(
    lectureDataForUpdate: LectureDataForUpdate
  ): Observable<boolean> {
    return this.put<boolean>("/lecture", lectureDataForUpdate, true).pipe(
      map((isSuccess: boolean) => isSuccess),
      catchError(() => of(false))
    );
  }

  /**
   * 특정 Lecture 에 다중 수강생 등록
   * @param usersDataForEnroll
   */
  public enrollUsers(
    usersDataForEnroll: UsersDataForEnroll
  ): Observable<boolean> {
    return this.post<boolean>(
      "/lecture/create/users",
      usersDataForEnroll,
      true
    ).pipe(
      map((isSuccess: boolean) => isSuccess),
      catchError(() => of(false))
    );
  }

  /**
   * 특정 Lecture 의 수강생 별 이수율 조회
   * @param id
   */
  public retrieveLectureProgressRateByUser(
    id: number
  ): Observable<LectureProgressRateByUserData[]> {
    return this.get<LectureProgressRateByUserData[]>(
      "/lecture/detail/student-progress-rate",
      {
        id
      },
      true
    ).pipe(
      map(
        (lectureProgressRateByUserDataList: LectureProgressRateByUserData[]) =>
          lectureProgressRateByUserDataList.map(lectureProgressRateByUserData =>
            LectureProgressRateByUserDataModel.create(
              lectureProgressRateByUserData
            )
          )
      ),
      catchError(() => of([]))
    );
  }

  /**
   * 특정 Lecture 의 설문 결과 조회
   * @param lectureId
   */
  public retrieveLectureSurvey(lectureId: string): Observable<any> {
    return this.get<any>("/survey/user/result", { lectureId }, true).pipe(
      map((list: any) => list),
      catchError(() => of([]))
    );
  }
}

export const lectureService = new LectureService();
